import { Box, Typography } from '@mui/material';
import StepApplication from '../components/Home/StepApplication';
import TaxSavings from '../components/Settings/TaxSavings';
import WealthImpactChart from '../components/Home/WealthImpactChart';
import Image from '../assets/images/no-strategy-image.png';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useEffect, useMemo, useState } from 'react';

import { Strategy } from '../Utilis/Types/Strategies';
import { StrategyService } from '../Service/StrategyService';
import { createNotification } from '../store/notifications/notification-slice';
import { Loading } from '../components/Loading';
import { EmptyList } from '../components/EmptyList';

const Home = () => {
  const authUser = useAppSelector(state => state.user.user);
  const client = useAppSelector(state => state.user.client);
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [showActive, setShowActive] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const appDispatch = useAppDispatch();

  const user = client || authUser;

  useEffect(() => {
    getStrategies();
  }, []);

  const getStrategies = async () => {
    setIsLoading(true);
    try {
      const { data } = await StrategyService.getStrategies();
      setStrategies(data);
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredStrategies = useMemo(() => {
    return strategies.filter(
      (strategy: Strategy) => strategy.completed === !showActive
    );
  }, [strategies, showActive]);

  const handleToggleChange = (event: boolean) => {
    setShowActive(event);
  };

  return (
    <Box>
      <Box sx={{ padding: '32px 32px 0 32px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '47px',
            marginBottom: '46px',
            '@media(max-width:768px)': {
              flexDirection: 'column',
            },
            '@media(max-width:1372px)': {
              gap: '20px',
              flexWrap: 'wrap',
            },
          }}
        >
          <TaxSavings
            estimatedTaxSaving={user?.estimated_tax_savings}
            lifeTimeImpact={user?.lifetime_impact_value}
          />

          <WealthImpactChart chart={user?.cashFlow!} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '2px solid #ACC7DC',
            position: 'relative',

            '::after': {
              content: '""',
              position: 'absolute',
              left: '50px',
              top: '42px',
              width: '0',
              height: '0',
              borderLeft: '20px solid transparent',
              borderRight: '20px solid transparent',
              borderTop: '20px solid #ACC7DC',
            },
          }}
        >
          <Typography
            color={'#202A2D'}
            fontWeight={'600'}
            fontSize={'24px'}
            lineHeight={'29px'}
            sx={{ marginBottom: '12px' }}
          >
            Next Steps
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '8px',
          }}
        >
          <Box
            sx={{
              border: '1px solid #E3DDE6',
              position: 'relative',
              borderRadius: '8px',
              zIndex: '0',
              padding: '10px 2px',
              maxWidth: '200px',
              width: '100%',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: '47%',
                height: '90%',
                background: '#acc7dc66',
                zIndex: '-1',
                borderRadius: '8px',
                transform: `translate(${showActive ? '0' : '108%'}, -50%)`,
                transition: '0.2s ease',
                top: '50%',
              }}
            />

            <Box
              sx={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{
                  width: '50%',
                  textAlign: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => handleToggleChange(true)}
                color={showActive ? '#202A2D' : '#655C68'}
                fontWeight={'500'}
                fontSize={'14px'}
                lineHeight={'17px'}
              >
                Active
              </Typography>
              <Typography
                sx={{
                  width: '50%',
                  textAlign: 'center',
                  cursor: 'pointer',
                  leadingTrim: 'both',
                }}
                onClick={() => handleToggleChange(false)}
                color={!showActive ? '#202A2D' : '#655C68'}
                fontWeight={'500'}
                fontSize={'14px'}
                lineHeight={'17px'}
              >
                Completed
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Loading isLoading={isLoading}>
        {filteredStrategies.length !== 0 ? (
          <Box marginTop={'20px'} p={'20px 32px'}>
            <Box
              sx={{
                display: 'grid',
                width: 'calc(100% - 44px)',
                gridTemplateColumns: '33.3% 33.3% 33.3%',
                '@media (max-width: 1268px)': {
                  gridTemplateColumns: '50% 50%',
                  width: 'calc(100% - 28px)',
                },
                '@media (max-width: 600px)': {
                  gridTemplateColumns: '100%',
                  width: '100%',
                },
              }}
              gap={'24px'}
            >
              {filteredStrategies.map((strategy: Strategy) => (
                <StepApplication
                  key={strategy.id}
                  id={strategy.id}
                  name={strategy.name}
                  steps={strategy.steps}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <EmptyList
            message={
              'You don’t have any active strategies yet.\nGet in touch with your Account Manager.'
            }
          />
        )}
      </Loading>
    </Box>
  );
};

export default Home;
